import React, { useEffect, useRef } from 'react'
import cloud from 'd3-cloud'
import * as d3 from 'd3'
import { iDados } from '../..'

interface WordData {
  text: string
  value: number
}

interface WordCloudProps {
  wordsData: iDados
  width?: number
  height?: number
  font?: string
  minFontSize?: number
  maxFontSize?: number
}

const WordCloud: React.FC<WordCloudProps> = ({
  wordsData,
  width = 740,
  height = 370,
  font = 'Roboto',
  minFontSize = 12,
  maxFontSize = 42
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null)

  const random = (() => {
    let seed = 42
    return () => {
      const x = Math.sin(seed++) * 10000
      return x - Math.floor(x)
    }
  })()

  const colorScale = d3.scaleOrdinal(d3.schemeCategory10)

  useEffect(() => {
    const words: WordData[] = []
    for (let i = 0; i < wordsData.labels.length; i++) {
      words.push({ text: wordsData.labels[i], value: wordsData.qtd[i] })
    }
    const layout = cloud()
      .size([width, height])
      .words(words.map(d => ({ text: d.text, size: d.value })))
      .padding(5)
      .rotate(() => 0)
      .font(font)
      .fontSize(d => scaleFontSize(d.size ?? 32))
      .random(random)
      .on('end', draw)

    const values = words.map(w => w.value)
    const min = Math.min(...values)
    const max = Math.max(...values)

    const scaleFontSize = d3.scaleLinear()
      .domain([min, max])
      .range([minFontSize, maxFontSize])

    layout.start()

    function draw(words: any[]): any {
      if (!svgRef.current) return

      const svg = d3.select(svgRef.current)
        .attr('width', width)
        .attr('height', height)

      svg.selectAll('*').remove()

      svg.append('g')
        .attr('transform', `translate(${width / 2}, ${height / 2})`)
        .selectAll('text')
        .data(words)
        .enter().append('text')
        .style('font-family', font)
        .style('font-size', d => `${d.size}px`)
        .style('font-weight', '900')
        .style('fill', () => d3.schemeCategory10[Math.floor(Math.random() * 10)])
        .attr('text-anchor', 'middle')
        .style('fill', (d, i) => colorScale(i.toString()))
        .attr('transform', d => `translate(${d.x}, ${d.y}) rotate(${d.rotate})`)
        .text(d => d.text)
    }
  }, [wordsData, width, height, font, minFontSize, maxFontSize])

  return <svg ref={svgRef} />
}

export default WordCloud
